// The EIN calendar. Two slots on every business day, each bound to a founder.

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useCurrentAdmin } from "@/hooks/useCurrentAdmin";

interface Founder {
  id: string;
  display_name: string;
  active: boolean;
}

interface Slot {
  id: string;
  slot_date: string;
  slot_index: number;
  founder_id: string;
  formation_id: string | null;
}

interface FormationRow {
  id: string;
  member_record_id: string;
  ein_scheduled_for: string | null;
  ein_acquired_on: string | null;
  member_records: {
    first_name: string | null;
    last_name: string | null;
    una_name: string | null;
  } | null;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

function monthDays(year: number, month: number): Date[] {
  const out: Date[] = [];
  const d = new Date(Date.UTC(year, month, 1));
  while (d.getUTCMonth() === month) {
    out.push(new Date(d));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

const isBusinessDay = (d: Date) => d.getUTCDay() >= 1 && d.getUTCDay() <= 5;

export default function EinCalendar() {
  const { isSuperAdmin } = useCurrentAdmin();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1)));
  const [founders, setFounders] = useState<Founder[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [formations, setFormations] = useState<FormationRow[]>([]);
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [autoAssign, setAutoAssign] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const year = cursor.getUTCFullYear();
  const month = cursor.getUTCMonth();
  const days = useMemo(() => monthDays(year, month).filter(isBusinessDay), [year, month]);

  const load = useCallback(async () => {
    setLoading(true);
    const first = iso(new Date(Date.UTC(year, month, 1)));
    const last = iso(new Date(Date.UTC(year, month + 1, 0)));

    const [f, s, fm, setting] = await Promise.all([
      (supabase as any).from("platform_founders").select("id, display_name, active").order("display_name"),
      (supabase as any)
        .from("ein_slots")
        .select("id, slot_date, slot_index, founder_id, formation_id")
        .gte("slot_date", first)
        .lte("slot_date", last),
      (supabase as any)
        .from("formations")
        .select("id, member_record_id, ein_scheduled_for, ein_acquired_on, member_records(first_name, last_name, una_name)")
        .is("ein_acquired_on", null)
        .limit(300),
      (supabase as any).from("app_settings").select("value").eq("key", "ein_auto_assign").maybeSingle(),
    ]);

    setFounders((f.data || []) as Founder[]);
    setSlots((s.data || []) as Slot[]);
    setFormations((fm.data || []) as FormationRow[]);
    setAutoAssign(String(setting.data?.value ?? "false") === "true");

    const { data: settingRows } = await (supabase as any)
      .from("app_settings")
      .select("key, value")
      .like("key", "ein_default_%");
    const map: Record<string, string> = {};
    for (const row of settingRows || []) map[String(row.key)] = String(row.value ?? "");
    setDefaults(map);
    setLoading(false);
  }, [year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeFounders = founders.filter((f) => f.active);

  const founderName = (id: string) => founders.find((f) => f.id === id)?.display_name ?? "Unassigned";

  const formationLabel = (id: string | null) => {
    if (!id) return null;
    const row = formations.find((f) => f.id === id);
    if (!row) return "Booked";
    const person = [row.member_records?.first_name, row.member_records?.last_name].filter(Boolean).join(" ");
    return row.member_records?.una_name || person || "Booked";
  };

  const unscheduled = formations.filter(
    (f) => !slots.some((s) => s.formation_id === f.id) && !f.ein_scheduled_for,
  );

  const ensureSlot = async (date: string, index: number, founderId: string) => {
    setBusy(true);
    const { error } = await (supabase as any)
      .from("ein_slots")
      .insert({ slot_date: date, slot_index: index, founder_id: founderId });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    void load();
  };

  const bookSlot = async (slot: Slot, formationId: string) => {
    setBusy(true);
    const { error } = await (supabase as any)
      .from("ein_slots")
      .update({ formation_id: formationId || null })
      .eq("id", slot.id);
    if (!error && formationId) {
      await (supabase as any)
        .from("formations")
        .update({ ein_scheduled_for: slot.slot_date, ein_responsible_founder_id: slot.founder_id })
        .eq("id", formationId);
    }
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Slot booked");
    void load();
  };

  const clearSlot = async (slot: Slot) => {
    setBusy(true);
    if (slot.formation_id) {
      await (supabase as any)
        .from("formations")
        .update({ ein_scheduled_for: null })
        .eq("id", slot.formation_id);
    }
    const { error } = await (supabase as any).from("ein_slots").update({ formation_id: null }).eq("id", slot.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    void load();
  };

  const saveDefault = async (key: string, value: string) => {
    const { error } = await (supabase as any)
      .from("app_settings")
      .upsert({ key, value }, { onConflict: "key" });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Saved");
  };

  const toggleAuto = async (next: boolean) => {
    setAutoAssign(next);
    const { error } = await (supabase as any)
      .from("app_settings")
      .upsert({ key: "ein_auto_assign", value: next ? "true" : "false" }, { onConflict: "key" });
    if (error) toast.error(error.message);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">EIN calendar</h1>
          <p className="text-sm text-muted-foreground">
            Two slots each business day. A founder holds one slot a day, never two.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="auto" className="text-sm">Assign the next open morning slot</Label>
          <Switch id="auto" checked={autoAssign} onCheckedChange={(v) => void toggleAuto(v)} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="icon"
          variant="outline"
          onClick={() => setCursor(new Date(Date.UTC(year, month - 1, 1)))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">
          {cursor.toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" })}
        </span>
        <Button
          size="icon"
          variant="outline"
          onClick={() => setCursor(new Date(Date.UTC(year, month + 1, 1)))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        {(loading || busy) && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Waiting for a slot</CardTitle>
          <CardDescription>
            {unscheduled.length === 0
              ? "Everyone in formation has a day."
              : `${unscheduled.length} waiting.`}
          </CardDescription>
        </CardHeader>
        {unscheduled.length > 0 && (
          <CardContent className="flex flex-wrap gap-2">
            {unscheduled.map((f) => (
              <Badge key={f.id} variant="outline">
                {formationLabel(f.id)}
              </Badge>
            ))}
          </CardContent>
        )}
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {days.map((day) => {
          const date = iso(day);
          const daySlots = slots.filter((s) => s.slot_date === date).sort((a, b) => a.slot_index - b.slot_index);
          return (
            <Card key={date}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {day.toLocaleDateString(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    timeZone: "UTC",
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[0, 1].map((index) => {
                  const slot = daySlots.find((s) => s.slot_index === index);
                  if (!slot) {
                    const taken = daySlots.map((s) => s.founder_id);
                    const free = activeFounders.filter((f) => !taken.includes(f.id));
                    return (
                      <div key={index} className="rounded-md border border-dashed p-2 text-xs">
                        <p className="mb-1 text-muted-foreground">
                          {index === 0 ? "Morning" : "Afternoon"} slot is not opened yet.
                        </p>
                        <Select onValueChange={(v) => void ensureSlot(date, index, v)}>
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Open it for a founder" />
                          </SelectTrigger>
                          <SelectContent>
                            {free.map((f) => (
                              <SelectItem key={f.id} value={f.id}>{f.display_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }
                  return (
                    <div key={slot.id} className="rounded-md border p-2 text-xs space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{index === 0 ? "Morning" : "Afternoon"}</span>
                        <span className="text-muted-foreground">{founderName(slot.founder_id)}</span>
                      </div>
                      {slot.formation_id ? (
                        <div className="flex items-center justify-between gap-2">
                          <span>{formationLabel(slot.formation_id)}</span>
                          <Button size="sm" variant="ghost" onClick={() => void clearSlot(slot)}>
                            Clear
                          </Button>
                        </div>
                      ) : (
                        <Select onValueChange={(v) => void bookSlot(slot, v)}>
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Book a member" />
                          </SelectTrigger>
                          <SelectContent>
                            {unscheduled.map((f) => (
                              <SelectItem key={f.id} value={f.id}>{formationLabel(f.id)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* The coding basis for every new formation. Pending counsel review. */}
      {!isSuperAdmin() ? (
        <Card>
          <CardHeader>
            <CardTitle>Standing answers for new formations</CardTitle>
            <CardDescription>This area is for the platform owner.</CardDescription>
          </CardHeader>
        </Card>
      ) : (
      <Card>
        <CardHeader>
          <CardTitle>Standing answers for new formations</CardTitle>
          <CardDescription>
            These fill a new formation record. Coding basis pending counsel.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          {[
            ["ein_default_org_type", "Organization type"],
            ["ein_default_activity_category", "Activity category"],
            ["ein_default_activity_subtype", "Activity subtype"],
            ["ein_default_activity_text", "Activity text"],
            ["ein_default_president", "Founding President"],
            ["ein_default_secretary", "Recording Secretary"],
          ].map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label>{label}</Label>
              <Input
                value={defaults[key] ?? ""}
                onChange={(e) => setDefaults((prev) => ({ ...prev, [key]: e.target.value }))}
                onBlur={(e) => void saveDefault(key, e.target.value)}
              />
            </div>
          ))}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
